import {MathUtils, Vector3, type Group, type PerspectiveCamera, type Vector2} from 'three'

import type {MonaCanvasMouseInteractionType, MonaCanvasOpeningAnimation} from '../types'
import type MainMona from './main-mona'
import type PointerTrail from './pointer-trail'

interface FollowInteractionScene {
  isResting: boolean
  isOpening: boolean
  isMouseInteractionBlocked: boolean
  hasMonaVisualBounds: boolean
  mouseInteractionType: MonaCanvasMouseInteractionType
  openingAnimation?: MonaCanvasOpeningAnimation
  onOpeningComplete?: () => void
  group: Group
  monaGroup: Group
  mainMona: MainMona
  fullViewportCamera: PerspectiveCamera
  pointerTrail: PointerTrail
  nextPointerPosition: Vector2
  mousePos: Vector2
  mousePosTarget: Vector2
  facingPos: Vector2
  facingPosTarget: Vector2
  facingRotationPos: Vector2
  facingRotationPosTarget: Vector2
  facingPointerPosition: Vector2
  pointerWorldPosition: Vector3
  facePosition: Vector3
  faceAnchorPosition: Vector3
  mouseMovementAmount: number
  horizontalTiltScale: number
  facingRotationScale: number
  facingPitchOffset: number
  updateOpening: (delta: number) => void
  updateMouseTargets: () => void
  updatePointerWorldPosition: (position: Vector2) => void
  getVisibleWorldHeight: (worldZ: number) => number
  resetPose: (pointerPosition?: Vector2) => void
}

const X_AXIS = new Vector3(1, 0, 0)
const Y_AXIS = new Vector3(0, 1, 0)
const Z_AXIS = new Vector3(0, 0, 1)
const VERTICAL_MOVEMENT_RANGE_SCALE = 1.5
const BOTTOM_MOVEMENT_RANGE_SCALE = 1.5
const FACING_YAW_STRENGTH = 6
const MAX_FACING_YAW = Math.PI * 0.5
const HORIZONTAL_TILT_STRENGTH = 3
const BANKING_RECOVERY = 3
const MAX_MOVEMENT_SPEED = 0.74
const MOVEMENT_SMOOTHING = 2
const POINTER_SMOOTHING = 3
const FACING_SMOOTHING = 3

export function getClampedPointerWorldY(
  pointerWorldY: number,
  viewportCenterY: number,
  visibleWorldHeight: number,
  cursorClearance: number,
) {
  const verticalLimit = Math.max(0, visibleWorldHeight * 0.5 * VERTICAL_MOVEMENT_RANGE_SCALE - cursorClearance)
  return MathUtils.clamp(
    pointerWorldY,
    viewportCenterY - verticalLimit * BOTTOM_MOVEMENT_RANGE_SCALE,
    viewportCenterY + verticalLimit,
  )
}

export function getHorizontalTiltScale(isOpening: boolean, currentScale: number, delta: number) {
  const smoothing = 1 - Math.exp(-delta * BANKING_RECOVERY)
  return MathUtils.lerp(currentScale, isOpening ? 0 : 1, smoothing)
}

export function getFacingLookAheadProgress(
  isOpening: boolean,
  stoppingProgress: number,
  waypointDistance: number,
  cursorClearance: number,
) {
  if (isOpening) return 1

  const facingLookAheadStartDistance = cursorClearance * 2
  return stoppingProgress * (1 - MathUtils.smoothstep(waypointDistance, cursorClearance, facingLookAheadStartDistance))
}

export function handleFollowPointer(scene: FollowInteractionScene, event: MouseEvent, rect: DOMRect) {
  scene.isResting = false
  scene.nextPointerPosition.set(
    MathUtils.clamp(((event.clientX - rect.left) / rect.width) * 2 - 1, -1, 1),
    MathUtils.clamp(1 - ((event.clientY - rect.top) / rect.height) * 2, -1, 1),
  )
  scene.pointerTrail.sample(scene.nextPointerPosition)
}

export function updateFollowPose(
  mona: MainMona,
  mousePos: Vector2,
  mouseMovementAmount: number,
  facingPos: Vector2,
  delta: number,
  horizontalTiltScale = 1,
  facingRotationScale = 1,
  facingPitchOffset = 0,
  facingRotationPos = facingPos,
) {
  const yaw = MathUtils.clamp(facingRotationPos.x * FACING_YAW_STRENGTH, -MAX_FACING_YAW, MAX_FACING_YAW)
  const verticalTilt = -facingPos.y * mona.verticalTiltStrength
  const downwardPitchStrength = 4
  const facingPitchStrength = facingRotationPos.y < 0 ? downwardPitchStrength : mona.verticalTiltStrength
  const facingPitch = -facingRotationPos.y * facingPitchStrength * facingRotationScale
  const verticalTiltSmoothingRate = 5
  const verticalTiltSmoothing = 1 - Math.exp(-delta * verticalTiltSmoothingRate)

  mona.facingQuaternion.setFromAxisAngle(Y_AXIS, yaw * facingRotationScale)
  mona.verticalTiltUniform.value = MathUtils.lerp(
    mona.verticalTiltUniform.value,
    Math.max(-0.0, verticalTilt),
    verticalTiltSmoothing,
  )

  mona.verticalTiltQuaternion.setFromAxisAngle(X_AXIS, facingPitch + mona.restingPitch + facingPitchOffset)
  mona.horizontalTiltQuaternion.setFromAxisAngle(Z_AXIS, -mousePos.x * HORIZONTAL_TILT_STRENGTH * horizontalTiltScale)
  mona.applyRotation()
  mona.updateMovement(mouseMovementAmount, delta)
}

export function updateFollowInteraction(scene: FollowInteractionScene, delta: number) {
  if (scene.isOpening) {
    if (!scene.hasMonaVisualBounds) return
    scene.updateOpening(delta)
  } else {
    scene.pointerTrail.update(delta)
  }
  scene.updateMouseTargets()

  const mouseMovementAmountTarget = MathUtils.clamp(
    Math.abs(scene.mousePosTarget.x - scene.mousePos.x) + Math.abs(scene.mousePosTarget.y - scene.mousePos.y),
    0,
    1,
  )
  const smoothing = 1 - Math.exp(-delta * POINTER_SMOOTHING)
  const movementSmoothing = 1 - Math.exp(-delta * MOVEMENT_SMOOTHING)
  const facingSmoothing = 1 - Math.exp(-delta * FACING_SMOOTHING)
  const movementDistance = scene.monaGroup.position.distanceTo(scene.pointerWorldPosition)
  let movementAlpha = movementDistance > 0 ? movementSmoothing : 0
  if (movementDistance > 0 && !(scene.isOpening && scene.openingAnimation?.disableMovementSpeedLimit)) {
    movementAlpha = Math.min(movementAlpha, (MAX_MOVEMENT_SPEED * delta) / movementDistance)
  }
  scene.monaGroup.position.lerp(scene.pointerWorldPosition, movementAlpha)
  scene.mousePos.lerp(scene.mousePosTarget, smoothing)
  scene.facingPos.lerp(scene.facingPosTarget, facingSmoothing)
  scene.facingRotationPos.lerp(scene.facingRotationPosTarget, facingSmoothing)
  scene.mouseMovementAmount = MathUtils.lerp(scene.mouseMovementAmount, mouseMovementAmountTarget, smoothing)

  const openingStopDistance = 0.002
  const openingMovementThreshold = 0.01
  const openingFacingThreshold = 0.0001
  const hasRestingFacingSettled =
    scene.openingAnimation?.restingYawRadians === undefined ||
    scene.facingRotationPos.distanceTo(scene.facingRotationPosTarget) <= openingFacingThreshold
  const hasOpeningStopped =
    scene.openingAnimation?.hasReachedEnd &&
    movementDistance <= openingStopDistance &&
    scene.mouseMovementAmount <= openingMovementThreshold &&
    hasRestingFacingSettled
  if (scene.isOpening && hasOpeningStopped) {
    scene.pointerTrail.clear()
    if (scene.openingAnimation) {
      scene.nextPointerPosition.copy(scene.openingAnimation.pointerPosition)
      scene.pointerTrail.sample(scene.nextPointerPosition)
    }
    scene.isOpening = false
    scene.isMouseInteractionBlocked = false
    if (scene.openingAnimation?.resetPoseOnComplete || scene.mouseInteractionType === 'face') scene.resetPose()
    else if (scene.openingAnimation?.restingYawRadians !== undefined) {
      const restingFacingX = scene.facingRotationPosTarget.x
      scene.resetPose(scene.openingAnimation.pointerPosition)
      scene.facingRotationPos.set(restingFacingX, 0)
    }
    scene.onOpeningComplete?.()
  }

  if (scene.isResting) {
    scene.mainMona.updateRestingPose(delta, scene.openingAnimation?.restingYawRadians ?? 0)
    return
  }

  const linearDeltaScaler = MathUtils.clamp(MathUtils.mapLinear(scene.mouseMovementAmount, 0, 0.1, 0, 1), 0, 1)
  const deltaScaler = 1 - (1 - linearDeltaScaler) ** 3

  scene.mainMona.commonUniforms.uTime.value += delta * deltaScaler
  scene.horizontalTiltScale = getHorizontalTiltScale(scene.isOpening, scene.horizontalTiltScale, delta)
  scene.facingRotationScale = MathUtils.lerp(
    scene.facingRotationScale,
    scene.isOpening ? (scene.openingAnimation?.facingRotationScale ?? 1) : 1,
    smoothing,
  )
  scene.facingPitchOffset = MathUtils.lerp(
    scene.facingPitchOffset,
    scene.isOpening ? (scene.openingAnimation?.facingPitchOffset ?? 0) : 0,
    smoothing,
  )
  updateFollowPose(
    scene.mainMona,
    scene.mousePos,
    scene.mouseMovementAmount,
    scene.facingPos,
    delta,
    scene.horizontalTiltScale,
    scene.facingRotationScale,
    scene.facingPitchOffset,
    scene.facingRotationPos,
  )
}

export function updateFollowTargets(
  scene: FollowInteractionScene,
  movementPointerPosition: Vector2,
  targetPointerPosition: Vector2,
) {
  scene.updatePointerWorldPosition(movementPointerPosition)

  const cursorClearance = 0.04
  const facingLookAhead = scene.isOpening ? 0.2 : 0.7
  const stoppingDistance = 0.2
  const waypointDistance = scene.pointerWorldPosition.distanceTo(scene.monaGroup.position)
  const stoppingProgress = 1 - MathUtils.smoothstep(scene.pointerTrail.remainingDistance, 0, stoppingDistance)
  const facingLookAheadProgress = getFacingLookAheadProgress(
    scene.isOpening,
    stoppingProgress,
    waypointDistance,
    cursorClearance,
  )

  const appliedCursorClearance = scene.isOpening ? 0 : cursorClearance * stoppingProgress
  if (appliedCursorClearance > 0) {
    if (waypointDistance <= appliedCursorClearance) {
      scene.pointerWorldPosition.copy(scene.monaGroup.position)
    } else {
      scene.pointerWorldPosition.lerp(scene.monaGroup.position, appliedCursorClearance / waypointDistance)
    }
  }
  if (!scene.isOpening) {
    scene.pointerWorldPosition.y = getClampedPointerWorldY(
      scene.pointerWorldPosition.y,
      scene.fullViewportCamera.position.y,
      scene.getVisibleWorldHeight(scene.pointerWorldPosition.z),
      cursorClearance,
    )
  }

  scene.facingPointerPosition.lerpVectors(movementPointerPosition, targetPointerPosition, stoppingProgress)

  scene.group.updateWorldMatrix(true, true)
  scene.mainMona.monaHeadGroup.getWorldPosition(scene.facePosition)
  scene.facePosition.project(scene.fullViewportCamera)

  const x = MathUtils.clamp((scene.facingPointerPosition.x - scene.facePosition.x) * 0.5, -0.5, 0.5)
  const y = MathUtils.clamp((scene.facingPointerPosition.y - scene.facePosition.y) * 0.5, -0.5, 0.5)

  scene.mousePosTarget.set(x, y)
  const facingLookAheadScale = 1 + facingLookAheadProgress * facingLookAhead
  scene.facingPosTarget.set(
    MathUtils.clamp(x * facingLookAheadScale, -0.5, 0.5),
    MathUtils.clamp(y * facingLookAheadScale, -0.5, 0.5),
  )

  scene.mainMona.faceAnchor.getWorldPosition(scene.faceAnchorPosition)
  scene.faceAnchorPosition.project(scene.fullViewportCamera)
  // Rotation follows the visible face; movement and shader motion retain their original pivot.
  const rotationX = MathUtils.clamp((scene.facingPointerPosition.x - scene.faceAnchorPosition.x) * 0.5, -0.5, 0.5)
  const rotationY = MathUtils.clamp((scene.facingPointerPosition.y - scene.faceAnchorPosition.y) * 0.5, -0.5, 0.5)
  scene.facingRotationPosTarget.set(
    MathUtils.clamp(rotationX * facingLookAheadScale, -0.5, 0.5),
    MathUtils.clamp(rotationY * facingLookAheadScale, -0.5, 0.5),
  )

  const opening = scene.openingAnimation
  if (scene.isOpening && opening?.restingYawRadians !== undefined) {
    const progress = opening.hasReachedEnd ? 1 : (opening.restingFacingProgress ?? 0)
    scene.facingRotationPosTarget.x = MathUtils.lerp(
      scene.facingRotationPosTarget.x,
      opening.restingYawRadians / FACING_YAW_STRENGTH,
      progress,
    )
    scene.facingRotationPosTarget.y *= 1 - progress
  }
}
